import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  FiltersState,
  New_TableOperator,
  PeriodType,
  SetFilters
} from '@lla-platform/core/core-data-access'
import { IMenuRouteInfo } from '@lla-platform/core/core-feature'
import {
  BookmarkType,
  IUser,
  Permission,
  Role,
  UserAccountState
} from '@lla-platform/users/users-data-access'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngxs/store'
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  fromEvent,
  map,
  Observable,
  startWith,
  switchMap,
  timer
} from 'rxjs'
import { GetUnreadAnnouncementsCount } from '@lla-platform/announcements/announcements-data-access'
import { ApplicationRoutes } from '../../../app-routes-names'
import { CustomersPaths } from '../../../features/user-panel/customers/customers-route-names'
import { ReceivablePaths } from '../../../features/user-panel/receivable/receivable-route-names'
import { InvoiceOperatedType } from '@lla-platform/receivable/receivable-data-access'
import { ShopPerformancePaths } from '../../../features/user-panel/shop-performance/shop-performance-route-names'
import { SamplesPaths } from '../../../features/user-panel/samples/samples-route-names'
import { TargetsPaths } from '../../../features/user-panel/targets/targets-route-names'
import { ReportsPaths } from '../../../features/user-panel/reports/reports-route-names'
import { environment } from '@lla-platform/core/core-util'
import { PaymentsPaths } from '../../../features/user-panel/payments/payments-route-names'
import { AdminPaths } from '../../../features/admin-panel/admin-route-names'
import { TiresPaths } from '../../../features/user-panel/tires/tires-route-names'
import { WorkOrdersPaths } from '../../../features/user-panel/work-orders/work-orders-route-names'
import { PromotionsPaths } from '../../../features/user-panel/promotions/promotions-route-names'
import { EmployeePaths } from '../../../features/user-panel/employee/employee-route-names'
import { VendorsPaths } from '../../../features/user-panel/vendors/vendors-route-names'
import { InfoPaths } from '../../../features/user-panel/info/info-route-names'
import { FluidsPaths } from '../../../features/user-panel/fluids/fluids-route-names'
import { AddonsPaths } from '../../../features/user-panel/addons/addons-route-names'
import { OfficialFormsPaths } from '../../../features/user-panel/official-forms/official-forms-route-names'
import { SchedulerPaths } from '../../../features/user-panel/scheduler/scheduler-route-names'
import { DashboardPaths } from '../../../features/user-panel/dashboard/dashboard-route-names'
import { LocationsPaths } from '../../../features/user-panel/locations/locations-route-names'
import { CallIntelligencePaths } from '../../../features/user-panel/call-intelligence/call-intelligence-route-names'

@UntilDestroy()
@Component({
  selector: 'lla-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MainLayoutComponent implements OnInit {
  isLoading$: Observable<boolean> = inject(Store).select(FiltersState.isLoading)
  isLoading = false
  currentUser$: Observable<IUser> = inject(Store).select(UserAccountState.currentUser)
  showLargeMenu$: Observable<boolean | undefined> = inject(Store).select(FiltersState.showLargeMenu)

  allRoles = Role
  showLargeMenu?: boolean = true

  userMainRoutes: IMenuRouteInfo[] = []

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.isLoading$.pipe(untilDestroyed(this), debounceTime(50)).subscribe((res) => {
      this.isLoading = !!res
    })

    // Polled here (not in announcements-menu) because the bell mounts twice
    // (mobile header + desktop sidebar) — component-level polling would double the requests.
    // Only polls while the tab is visible: a backgrounded tab polling every 90s burns requests and,
    // once the access token expires, every one of those polls triggers a token refresh. Becoming
    // visible again polls immediately, so the badge is current the moment the user looks at it.
    fromEvent(document, 'visibilitychange')
      .pipe(
        map(() => !document.hidden),
        startWith(!document.hidden),
        distinctUntilChanged(),
        switchMap((isVisible) => (isVisible ? timer(0, 90000) : EMPTY)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.store.dispatch(new GetUnreadAnnouncementsCount())
      })

    this.showLargeMenu$.pipe(untilDestroyed(this)).subscribe((show) => {
      this.showLargeMenu = show
    })

    this.store.dispatch(
      new SetFilters({
        listPagesPath: {
          [BookmarkType.Receivable]: ReceivablePaths.receivablesList,
          [BookmarkType.Invoice]: ShopPerformancePaths.invoicesList,
          [BookmarkType.Customer]: CustomersPaths.report,
          [BookmarkType.Payment]: PaymentsPaths.paymentsList,
          [BookmarkType.Tire]: TiresPaths.list,
          [BookmarkType.InvoiceGrossProfit]: ShopPerformancePaths.grossProfitInvoicesList,
          [BookmarkType.InvoicePackageGrossProfit]: ShopPerformancePaths.gpPackageInvoicesList,
          [BookmarkType.LostTiresReport]: TiresPaths.lostTiresList,
          [BookmarkType.WorkOrders]: WorkOrdersPaths.list,
          [BookmarkType.Employee]: EmployeePaths.list,
          [BookmarkType.Fluids]: FluidsPaths.summary
        }
      })
    )

    this.currentUser$.pipe(untilDestroyed(this)).subscribe((user) => {
      const userRole = user?.role
      const userPermissions = user?.permissions ?? []
      this.userMainRoutes = [
        {
          label: 'Dashboard',
          icon: 'dashboard',
          type: 'service',
          subMenus: [
            {
              label: 'Overview',
              icon: 'redo',
              path: ApplicationRoutes.dashboard,
              exact: true
            },
            {
              label: 'Week over Week',
              icon: 'redo',
              path: DashboardPaths.weekOverWeek
            },
            {
              label: 'Location Status Board',
              icon: 'redo',
              path: LocationsPaths.status
            }
          ]
        },
        {
          label: 'LLA Objectives',
          icon: 'target',
          type: 'service',
          subMenus: [
            {
              label: 'Target Objectives',
              icon: 'redo',
              path: TargetsPaths.objectives
            },
            {
              label: 'Weekly Target Rankings',
              icon: 'redo',
              path: TargetsPaths.rankings
            },
            {
              label: 'Period Target Rankings',
              icon: 'redo',
              path: TargetsPaths.periodRankings
            },
            {
              label: 'Objectives MA',
              icon: 'redo',
              path: ReportsPaths.movingAverage,
              hidden: !userPermissions.includes(Permission.Reports)
            },
            {
              label: 'History',
              icon: 'redo',
              path: TargetsPaths.history
            },
            {
              label: 'Objectives vs Comp',
              icon: 'redo',
              path: TargetsPaths.objectivesVsComp,
              hidden: !userPermissions.includes(Permission.ObjectiveComps)
            },
            {
              label: 'Monthly Comp',
              icon: 'redo',
              path: ReportsPaths.compare,
              clickFn: () => {
                this.store.dispatch(
                  new SetFilters({
                    compareReportGetData: true,
                    compareReportFilter: undefined,
                    compareReportSelectedObjectives: undefined,
                    compareReportDisplayOptions: undefined,
                    compareReportExpandedShopsId: undefined,
                    compareReportShowCharts: undefined
                  })
                )
              }
            },
            {
              label: 'Weekly Average',
              icon: 'redo',
              path: TargetsPaths.weeklyAverage
            },
            {
              label: 'Yearly Targets',
              icon: 'redo',
              path: TargetsPaths.yearlyTargets,
              hidden: !userPermissions.includes(Permission.YearlyTargets)
            }
          ]
        },
        {
          label: 'Attendance',
          icon: 'users-circle-check',
          type: 'service',
          hidden:
            !userPermissions.includes(Permission.Attendance) &&
            !userPermissions.includes(Permission.ScheduleShopManager) &&
            !userPermissions.includes(Permission.ScheduleRegionalManager),
          subMenus: [
            {
              label: 'Attendance',
              icon: 'redo',
              path: `/${ApplicationRoutes.attendance}`,
              hidden: !userPermissions.includes(Permission.Attendance)
            },
            {
              label: 'Schedule',
              icon: 'redo',
              path: SchedulerPaths.list,
              hidden:
                !userPermissions.includes(Permission.ScheduleShopManager) &&
                !userPermissions.includes(Permission.ScheduleRegionalManager)
            },
            {
              label: 'Attendance vs. Schedule',
              icon: 'redo',
              path: SchedulerPaths.attendance,
              hidden:
                !userPermissions.includes(Permission.ScheduleShopManager) &&
                !userPermissions.includes(Permission.ScheduleRegionalManager)
            }
          ]
        },
        {
          label: 'Events Calendar',
          icon: 'calendar',
          type: 'service',
          path: `/${ApplicationRoutes.eventsCalendar}`,
          hidden:
            !userPermissions.includes(Permission.EventsCalendarRM) &&
            !userPermissions.includes(Permission.EventsCalendarDM) &&
            !userPermissions.includes(Permission.EventsCalendarAdmin)
        },
        {
          label: 'Work Orders',
          icon: 'stack',
          type: 'service',
          path: ApplicationRoutes.workOrders,
          hidden: !userPermissions.includes(Permission.WorkOrders)
        },
        {
          label: 'Tires',
          icon: 'wheel',
          type: 'service',
          hidden:
            !userPermissions.includes(Permission.Tires) &&
            !userPermissions.includes(Permission.TiresManagement),
          subMenus: [
            {
              label: 'By Programs',
              icon: 'redo',
              path: TiresPaths.summaryProgram,
              hidden: !userPermissions.includes(Permission.Tires)
            },
            {
              label: 'By Levels',
              icon: 'redo',
              path: TiresPaths.summarylevels,
              hidden: !userPermissions.includes(Permission.Tires)
            },
            {
              label: 'By Stockable',
              icon: 'redo',
              path: TiresPaths.summaryStockable,
              hidden: !userPermissions.includes(Permission.Tires)
            },
            {
              label: 'By Rotation',
              icon: 'redo',
              path: TiresPaths.summaryRotation,
              hidden: !userPermissions.includes(Permission.Tires)
            },
            {
              label: 'By Payment',
              icon: 'redo',
              path: TiresPaths.summaryPayment,
              hidden: !userPermissions.includes(Permission.Tires)
            },
            {
              label: 'Stockable Items',
              icon: 'redo',
              path: ApplicationRoutes.stockable,
              hidden: !userPermissions.includes(Permission.TiresManagement)
            },
            {
              label: 'Inventory Summary',
              icon: 'redo',
              path: TiresPaths.inventorySummary,
              hidden: !userPermissions.includes(Permission.TiresManagement)
            },
            {
              label: 'Order Tires',
              icon: 'redo',
              hidden: !userPermissions.includes(Permission.TiresManagement),
              subMenus: [
                {
                  label: 'By Location',
                  icon: 'redo',
                  path: TiresPaths.order,
                  hidden: !userPermissions.includes(Permission.TiresManagement)
                },
                {
                  label: 'Refill All',
                  icon: 'redo',
                  path: TiresPaths.orderRefill,
                  hidden: !userPermissions.includes(Permission.TiresManagement)
                }
              ]
            },
            {
              label: 'USAF Orders',
              icon: 'redo',
              path: TiresPaths.usafOverview,
              hidden: !userPermissions.includes(Permission.TiresManagement),
              clickFn: () => {
                this.store.dispatch(
                  new SetFilters({
                    usafOverviewGetData: true,
                    usafOverviewFilters: {
                      filters: {
                        orderDate: {
                          key: 'orderDate',
                          items: [
                            {
                              operator: New_TableOperator.Period,
                              value: PeriodType.MonthToDate
                            }
                          ]
                        }
                      },
                      page: 1,
                      sort: {
                        active: 'orderDate',
                        direction: 'desc'
                      }
                    }
                  })
                )
              }
            },
            {
              label: 'Weekly Tire Report',
              icon: 'redo',
              path: OfficialFormsPaths.weeklyTireReportSummary,
              hidden: environment.production || !userPermissions.includes(Permission.TiresManagement)
            }
          ]
        },
        {
          label: 'Trackers',
          icon: 'trend',
          type: 'service',
          path: ApplicationRoutes.tracker,
          hidden: !userPermissions.includes(Permission.Tracker)
        },
        {
          label: 'Call Intelligence',
          icon: 'stars-ai',
          type: 'service',
          subMenus: [
            {
              label: 'Summary',
              icon: 'redo',
              path: CallIntelligencePaths.dashboard,
              hidden: !userPermissions.includes(Permission.CallIntelligence)
            },
            {
              label: 'Local Service Ad',
              icon: 'redo',
              path: CallIntelligencePaths.drilldown,
              hidden:
                environment.production || !userPermissions.includes(Permission.CallIntelligenceDrilldown)
            }
          ]
        },
        {
          label: 'Official Forms',
          icon: 'ballot',
          type: 'service',
          hidden: !userPermissions.includes(Permission.ScheduleRegionalManager),
          subMenus: [
            {
              label: 'Cash Drawer Count Sheet',
              icon: 'redo',
              path: OfficialFormsPaths.cashDrawerSummary,
              hidden: environment.production
            },
            {
              label: 'Weekly Tire Count Sheet',
              icon: 'redo',
              path: OfficialFormsPaths.weeklyTireCountSummary,
              hidden: environment.production
            },
            {
              label: 'Curb Appeal Inspection',
              icon: 'redo',
              path: OfficialFormsPaths.curbAppealSummary,
              hidden: !userPermissions.includes(Permission.ScheduleRegionalManager)
            },
            {
              label: 'Showroom Inspection',
              icon: 'redo',
              path: OfficialFormsPaths.showroomSummary,
              hidden: !userPermissions.includes(Permission.ScheduleRegionalManager)
            },
            {
              label: 'Inspection Compliance',
              icon: 'redo',
              path: OfficialFormsPaths.inspectionCompliance,
              hidden: !userPermissions.includes(Permission.ScheduleRegionalManager)
            }
          ]
        },
        {
          label: 'Invoices',
          icon: 'file-text',
          type: 'service',
          hidden: !userPermissions.includes(Permission.Invoices),
          subMenus: [
            {
              label: 'Overview',
              icon: 'redo',
              path: ShopPerformancePaths.invoicesListOverview,
              exact: true,
              hidden: !userPermissions.includes(Permission.Invoices),
              clickFn: () => {
                this.store.dispatch(
                  new SetFilters({
                    invoicesListGetData: true,
                    invoicesListFilters: {
                      filters: {
                        invoiceDate: {
                          key: 'invoiceDate',
                          items: [
                            {
                              operator: New_TableOperator.Period,
                              value: PeriodType.WeekToDate
                            }
                          ]
                        },
                        operated: {
                          key: 'operated',
                          items: [
                            {
                              operator: New_TableOperator.Equals,
                              value: [InvoiceOperatedType.OperatedByLLA]
                            }
                          ]
                        }
                      },
                      page: 1,
                      sort: {
                        active: 'invoiceDate',
                        direction: 'desc'
                      }
                    }
                  })
                )
              }
            },
            {
              label: 'Lines',
              icon: 'redo',
              path: ShopPerformancePaths.invoiceLinesOverview,
              hidden: !userPermissions.includes(Permission.Invoices),
              // The report pages apply their own default period
              clickFn: () => {
                this.store.dispatch(
                  new SetFilters({ invoiceLinesGetData: true, invoiceLinesFilters: { page: 1 } })
                )
              }
            },
            {
              label: 'By Customer Type',
              icon: 'redo',
              path: ShopPerformancePaths.invoicesCustomerType,
              hidden: !userPermissions.includes(Permission.Invoices)
            },
            {
              label: 'By Gross Profit',
              icon: 'redo',
              path: ShopPerformancePaths.invoicesGrossProfit,
              hidden: !userPermissions.includes(Permission.Invoices)
            },
            {
              label: 'By Deferred Type',
              icon: 'redo',
              path: ShopPerformancePaths.invoicesDeferredType,
              hidden: !userPermissions.includes(Permission.Invoices)
            },
            {
              label: 'Categories MA',
              icon: 'redo',
              path: ShopPerformancePaths.categoriesMovingAverage,
              hidden: !userPermissions.includes(Permission.Invoices)
            },
            {
              label: 'Category Overview',
              icon: 'redo',
              path: ShopPerformancePaths.categoryOverview,
              hidden: !userPermissions.includes(Permission.Invoices)
            }
          ]
        },
        {
          label: 'Packages GP',
          icon: 'inventory',
          type: 'service',
          hidden: !userPermissions.includes(Permission.PackageGp),
          subMenus: [
            {
              label: 'By Location',
              icon: 'redo',
              path: ShopPerformancePaths.gpPackageLocation,
              hidden: !userPermissions.includes(Permission.PackageGp)
            },
            {
              label: 'By Category',
              icon: 'redo',
              path: ShopPerformancePaths.gpPackageCategory,
              hidden: !userPermissions.includes(Permission.PackageGp)
            },
            {
              label: 'By Performance',
              icon: 'redo',
              path: ShopPerformancePaths.performancePackageGp,
              hidden: !userPermissions.includes(Permission.PackageGp)
            }
          ]
        },
        {
          label: 'Contacts',
          icon: 'contacts',
          type: 'service',
          hidden: !userPermissions.includes(Permission.Contacts),
          subMenus: [
            {
              label: 'Contact List Generator',
              icon: 'redo',
              path: CustomersPaths.report,
              hidden: !userPermissions.includes(Permission.Contacts)
            },
            {
              label: 'Phone/Email Overview',
              icon: 'redo',
              path: CustomersPaths.communicationType,
              hidden: !userPermissions.includes(Permission.Contacts)
            }
          ]
        },
        {
          label: 'Reports',
          icon: 'chart',
          type: 'service',
          hidden:
            !userPermissions.includes(Permission.Reports) &&
            !userPermissions.includes(Permission.LostTiresReport),
          subMenus: [
            {
              label: 'Lost Tires',
              icon: 'redo',
              path: TiresPaths.lostTiresList,
              hidden: !userPermissions.includes(Permission.LostTiresReport)
            },
            {
              label: 'Sales',
              icon: 'redo',
              path: ReportsPaths.sales,
              hidden: !userPermissions.includes(Permission.Reports)
            },
            {
              label: 'Tires',
              icon: 'redo',
              path: ReportsPaths.tires,
              hidden: !userPermissions.includes(Permission.Reports)
            },
            {
              label: 'Fluids',
              icon: 'redo',
              path: FluidsPaths.summary,
              hidden: !userPermissions.includes(Permission.Reports)
            },
            {
              label: 'Addons',
              icon: 'redo',
              path: AddonsPaths.summary,
              hidden: !userPermissions.includes(Permission.Reports)
            },
            {
              label: 'Upsell',
              icon: 'redo',
              path: ReportsPaths.upsell,
              hidden: !userPermissions.includes(Permission.UpsellReport),
              clickFn: () => {
                this.store.dispatch(
                  new SetFilters({
                    upsellFilter: undefined
                  })
                )
              }
            },
            {
              label: 'Daily Average',
              icon: 'redo',
              path: ReportsPaths.dailyAverage,
              hidden: environment.production || !userPermissions.includes(Permission.Reports)
            },
            {
              label: 'AdjGP Dashboard',
              icon: 'redo',
              path: ReportsPaths.adjustedGP,
              hidden: !userPermissions.includes(Permission.AdjGP)
            },
            {
              label: 'Google Reviews',
              icon: 'redo',
              path: ReportsPaths.googleReviews
            },
            {
              label: 'Oil Change',
              icon: 'redo',
              path: ReportsPaths.oilChange,
              hidden: !userPermissions.includes(Permission.Reports)
            }
          ]
        },
        {
          label: 'BCap',
          icon: 'bc',
          type: 'service',
          hidden: !userPermissions.includes(Permission.BCap),
          subMenus: [
            {
              label: 'By Group',
              icon: 'redo',
              path: ReportsPaths.compareObjectives,
              hidden: !userPermissions.includes(Permission.BCap)
            },
            {
              label: 'By Regional',
              icon: 'redo',
              path: ReportsPaths.compareObjectivesRegional,
              hidden: !userPermissions.includes(Permission.BCap)
            }
          ]
        },
        {
          label: 'Reviews',
          icon: 'check-list',
          type: 'service',
          path: ApplicationRoutes.reviews,
          hidden: !userPermissions.includes(Permission.Reviews)
        },
        {
          label: 'Lost Sales Tire Tracker',
          icon: 'link-break',
          type: 'service',
          path: TiresPaths.lostTire,
          hidden: !userPermissions.includes(Permission.LostSales)
        },
        {
          label: 'Who to Call?',
          icon: 'phone',
          type: 'service',
          path: `/${ApplicationRoutes.whoToCall}`
        },
        {
          label: 'Employee Resources',
          icon: 'file-text',
          type: 'service',
          path: InfoPaths.publicResources
        },
        {
          label: 'Promotions',
          icon: 'price-tag',
          type: 'service',
          path: PromotionsPaths.list
        },
        {
          label: 'Manage Promotions',
          icon: 'price-tag',
          type: 'service',
          path: PromotionsPaths.promotionsList,
          hidden: !userPermissions.includes(Permission.ManagePromotions)
        },
        {
          label: 'Receivables',
          icon: 'receive-money',
          type: 'service',
          hidden: !userPermissions.includes(Permission.Receivables),
          subMenus: [
            {
              label: 'Locations',
              icon: 'redo',
              path: ReceivablePaths.shopsSummary,
              hidden: !userPermissions.includes(Permission.Receivables)
            },
            {
              label: 'Accounts',
              icon: 'redo',
              path: ReceivablePaths.accountsSummary,
              hidden: !userPermissions.includes(Permission.Receivables)
            },
            {
              label: 'AR Sales',
              icon: 'redo',
              path: ReceivablePaths.arInvoicesSummary,
              hidden: !userPermissions.includes(Permission.Receivables)
            },
            {
              label: 'Receivables MA',
              icon: 'redo',
              path: ReceivablePaths.arMovingAverage,
              hidden: !userPermissions.includes(Permission.Receivables)
            },
            {
              label: 'AR Payments',
              icon: 'redo',
              path: ReceivablePaths.arPaymentsSummary,
              hidden: !userPermissions.includes(Permission.Receivables)
            }
          ]
        },
        {
          label: 'Vendors',
          icon: 'shop-store',
          type: 'service',
          subMenus: [
            {
              label: 'Summary',
              icon: 'redo',
              path: VendorsPaths.summary
            },
            {
              label: 'Levels Summary',
              icon: 'redo',
              path: VendorsPaths.levels
            },
            {
              label: 'Preferred Vendors',
              icon: 'redo',
              path: VendorsPaths.preferred,
              hidden: !userPermissions.includes(Permission.PreferredVendors)
            }
          ]
        },
        {
          label: 'Employees',
          icon: 'users-bold',
          type: 'service',
          subMenus: [
            {
              label: 'Employees List',
              icon: 'redo',
              path: EmployeePaths.list,
              hidden: !userPermissions.includes(Permission.EmployeeManagement)
            },
            {
              label: 'Labor Summary',
              icon: 'redo',
              path: EmployeePaths.laborSummary
            },
            {
              label: 'Back Shop Efficiency',
              icon: 'redo',
              path: EmployeePaths.technicianSummary
            },
            {
              label: 'Front Shop Efficiency',
              icon: 'redo',
              path: EmployeePaths.serviceAdvisorSummary
            },
            {
              label: 'Employee MA',
              icon: 'redo',
              path: EmployeePaths.employeeEfficiencyMovingAverage,
              hidden: environment.production
            }
          ]
        },
        {
          label: 'Comps (soon)',
          icon: 'grid',
          path: '',
          type: 'service',
          exact: true,
          hidden: environment.production
        },
        {
          label: 'Samples (Demo Only)',
          icon: 'chart',
          type: 'service',
          hidden: environment.production,
          subMenus: [
            {
              label: 'Doughnut Chart',
              icon: 'redo',
              path: SamplesPaths.doughnutChart,
              hidden: environment.production
            },
            {
              label: 'Sankey Chart',
              icon: 'redo',
              path: SamplesPaths.sankeyChart,
              hidden: environment.production
            },
            {
              label: 'Map',
              icon: 'redo',
              path: SamplesPaths.map,
              hidden: environment.production
            },
            {
              label: 'Location Map',
              icon: 'redo',
              path: SamplesPaths.shopMap,
              hidden: environment.production
            },
            {
              label: 'Map Clustering',
              icon: 'redo',
              path: SamplesPaths.mapClustering,
              hidden: environment.production
            },
            {
              label: 'Map Markers',
              icon: 'redo',
              path: SamplesPaths.mapMarkers,
              hidden: environment.production
            }
          ]
        },
        {
          label: 'Admin',
          icon: 'settings',
          type: 'admin',
          hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin),
          subMenus: [
            {
              label: 'Location Targets',
              icon: 'target',
              type: 'admin',
              subMenus: [
                {
                  label: 'Yearly Targets',
                  icon: 'redo',
                  path: AdminPaths.yearlyTargets,
                  hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
                },
                {
                  label: 'View Targets',
                  icon: 'redo',
                  path: AdminPaths.viewTargets,
                  hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
                },
                {
                  label: 'AdjGP Targets',
                  icon: 'redo',
                  path: AdminPaths.monthlyTargets,
                  hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin),
                  clickFn: () => {
                    const currentMonth = new Date().getMonth() + 1
                    this.store.dispatch(
                      new SetFilters({
                        adminMonthlyTargetsFilters: {
                          filters: {
                            monthNumber: {
                              key: 'monthNumber',
                              items: [
                                {
                                  operator: New_TableOperator.Equals,
                                  value: currentMonth
                                }
                              ]
                            }
                          },
                          page: 1,
                          sort: {
                            active: 'locationName',
                            direction: 'asc'
                          }
                        }
                      })
                    )
                  }
                }
              ]
            },
            {
              label: 'Thresholds',
              icon: 'car-category',
              type: 'admin',
              subMenus: [
                {
                  label: 'Category Margins',
                  icon: 'redo',
                  path: AdminPaths.setMargins,
                  hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
                },
                {
                  label: 'Efficiency Targets',
                  icon: 'redo',
                  path: AdminPaths.efficiencyTargets,
                  hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
                },
                {
                  label: 'Inspection Windows',
                  icon: 'redo',
                  path: AdminPaths.inspectionWindows,
                  hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
                }
              ]
            },
            {
              label: 'Users',
              icon: 'users-bold',
              path: AdminPaths.userList,
              hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
            },
            {
              label: 'Locations',
              icon: 'garage',
              path: AdminPaths.locationsList,
              hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
            },
            {
              label: 'Salaries Summary',
              icon: 'dollar-circle',
              path: AdminPaths.salariesSummary,
              hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
            },
            {
              label: 'Surveys',
              icon: 'check-list',
              path: AdminPaths.surveyList,
              hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
            },
            {
              label: 'Announcements',
              icon: 'bell',
              path: AdminPaths.announcementsList,
              hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
            },
            {
              label: 'Status Proposals',
              icon: 'redo',
              path: AdminPaths.statusProposals,
              hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
            },
            {
              label: 'Call Tags',
              icon: 'stars-ai',
              path: AdminPaths.callTags,
              hidden: userRole !== Role.admin && !userPermissions.includes(Permission.Admin)
            },
            {
              label: 'Queue Messages',
              icon: 'redo',
              path: AdminPaths.queueMessages,
              hidden:
                environment.production ||
                (userRole !== Role.admin && !userPermissions.includes(Permission.Admin))
            }
          ]
        }
      ]
    })
  }

  onShowLargeMenuChange(state: boolean) {
    this.showLargeMenu = state
    this.store.dispatch(
      new SetFilters({
        showLargeMenu: this.showLargeMenu
      })
    )
  }
}
